/**
 * @file components/audit-tasks/DispatchConfirmPanel.tsx
 * @description 派案確認卡片（序63 派案確認強化 / 序64 預計日期遷移）。
 *
 * 稽核員收到派案後，在此做兩選一：
 *  1）接收案件：為稽核小組成員指定角色（主導稽核員 / 稽核員 / 實習稽核員 / 技術專家 /
 *     觀察員 / 驗證助理）、可「添加稽核小組成員」（序18；序27 限稽核員帳號、角色固定為稽核員）、
 *     主導稽核員確認簽名（序26），反饋管理組。
 *     2026-09-15 回報序28：【預定稽核日期】【稽核人天數】已移至「稽核計畫」階段填寫與修改，
 *     接收案件時不再填。
 *  2）不接收案件：填【驗證組備註】說明原因，提交後通知管理組。
 *
 * 說明：
 *  - 序64：預計稽核日期由開案準備遷移至此（接收案件時填）；SchedulePanel 降級為
 *    「改期 / 歷史查看」（序63.4 改期仍在該卡片操作，仍會通知管理組）。
 *  - 僅任務 assignee 本人可操作（後端亦強制校驗 audit-task.confirm 能力）。
 *  - 已回覆（dispatchResponse != null）後展示回覆結果摘要，不再重複提供表單。
 *
 * 數據源：
 *  - GET  /api/audit-tasks/:id/team-candidates  添加稽核小組成員候選（序18）
 *  - POST /api/audit-tasks/:id/confirm          接收案件
 *  - POST /api/audit-tasks/:id/reject-dispatch  不接收案件
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { CheckCircle2, Plus, Trash2, XCircle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DISPATCH_TEAM_MEMBER_ROLES,
  DISPATCH_TEAM_MEMBER_ROLE_LABELS,
  dispatchTeamRoleFromLabel,
  type AuditTaskDetail,
  type DispatchTeamCandidate,
  type DispatchTeamMemberRole,
} from '@zhcb/shared';
import { apiClient, ApiError } from '@/lib/api-client';
import { uploadFile } from '@/lib/file-upload';
import { useAuthStore } from '@/lib/auth-context';
import { message } from '@/lib/antd-app';
import { SignaturePadModal } from '@/components/common/SignaturePadModal';

interface Props {
  task: AuditTaskDetail;
}

/** 接收案件表單值 */
interface AcceptFormValues {
  /** 各成員角色映射（key = userId 字串） */
  memberRoles: Record<string, DispatchTeamMemberRole>;
}

/** 不接收案件表單值 */
interface RejectFormValues {
  note: string;
}

/**
 * 既有 case_audit_team.role 標籤 → 派案確認角色（用於預填）。
 * 「稽核組長」「主導稽核員」皆對應主導稽核員；無法辨識時回落稽核員。
 */
function labelToRole(label: string | null | undefined): DispatchTeamMemberRole {
  return dispatchTeamRoleFromLabel(label) ?? 'auditor';
}

export function DispatchConfirmPanel({ task }: Props) {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  /** 僅任務 assignee 本人可操作（後端亦強制） */
  const isAssignee = userId != null && userId === task.assigneeId;
  /** 尚未回覆才顯示表單（已接收 → 已確認狀態；不接收 → dispatchResponse='rejected'） */
  const alreadyResponded = task.dispatchResponse != null;

  const [mode, setMode] = useState<'accept' | 'reject'>('accept');
  const [acceptForm] = Form.useForm<AcceptFormValues>();
  const [rejectForm] = Form.useForm<RejectFormValues>();
  const [modalOpen, setModalOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);
  /** 稽核員確認簽名上傳後的 fileId（接收案件時必填） */
  const [signatureFileId, setSignatureFileId] = useState<number | null>(null);

  /**
   * 派案確認時新添加的稽核小組成員（序18）：尚未落庫，隨「接收並提交」一併送出，
   * 服務端以 (caseId, userId) upsert 進 case_audit_team。
   */
  const [addedMembers, setAddedMembers] = useState<{ userId: number; userName: string }[]>([]);
  /** 「添加稽核小組成員」選擇器當前選中的員工 */
  const [pendingAddUserId, setPendingAddUserId] = useState<number | undefined>();

  /** 稽核小組＝本案已派遣的成員（含本人）＋本次新添加的成員 */
  const teamMembers = useMemo(
    () => [
      ...task.teammates.map((m) => ({ userId: m.userId, userName: m.userName, role: m.role })),
      ...addedMembers.map((m) => ({ userId: m.userId, userName: m.userName, role: null })),
    ],
    [task.teammates, addedMembers],
  );
  /** 已在小組中的成員 id（用於從候選清單排除，避免重複添加） */
  const teamMemberIds = useMemo(() => new Set(teamMembers.map((m) => m.userId)), [teamMembers]);

  /** 添加成員候選（在職員工，排除本案既有成員；僅在開啟回覆彈窗時載入） */
  const candidatesQuery = useQuery({
    queryKey: ['audit-task-team-candidates', task.id],
    queryFn: () =>
      apiClient.get<{ items: DispatchTeamCandidate[] }>(
        `/api/audit-tasks/${task.id}/team-candidates`,
      ),
    enabled: modalOpen && isAssignee,
  });
  const candidateOptions = (candidatesQuery.data?.items ?? [])
    .filter((c) => !teamMemberIds.has(c.id))
    .map((c) => ({ value: c.id, label: c.name }));

  /** 把選擇器中的員工加入小組（預設角色「稽核員」，可再於下方調整） */
  const handleAddMember = () => {
    if (pendingAddUserId == null) return;
    const hit = candidatesQuery.data?.items.find((c) => c.id === pendingAddUserId);
    if (!hit) return;
    setAddedMembers((prev) => [...prev, { userId: hit.id, userName: hit.name }]);
    setPendingAddUserId(undefined);
  };

  /** 移除本次新添加（尚未提交）的成員；既有成員不可在此移除 */
  const handleRemoveAddedMember = (userId: number) => {
    setAddedMembers((prev) => prev.filter((m) => m.userId !== userId));
    acceptForm.setFieldValue(['memberRoles', String(userId)], undefined);
  };
  /** 是否為本次新添加（尚未提交）的成員 */
  const isAddedMember = (userId: number) => addedMembers.some((m) => m.userId === userId);

  /** 接收案件 Mutation */
  const acceptMutation = useMutation({
    mutationFn: (values: AcceptFormValues) =>
      apiClient.post<{ ok: boolean }>(`/api/audit-tasks/${task.id}/confirm`, {
        // 序28：預定稽核日期 / 人天數移至稽核計畫階段填寫，接收案件不再傳
        signatureFileId: signatureFileId ?? undefined,
        teamMembers: teamMembers.map((m) => ({
          userId: m.userId,
          // 序27：新添加的成員角色固定為稽核員
          role: isAddedMember(m.userId)
            ? 'auditor'
            : (values.memberRoles?.[String(m.userId)] ?? labelToRole(m.role)),
        })),
      }),
    onSuccess: () => {
      message.success('已接收案件並通知管理組');
      setModalOpen(false);
      acceptForm.resetFields();
      setSignatureFileId(null);
      setAddedMembers([]);
      void qc.invalidateQueries({ queryKey: ['audit-task-detail', task.id] });
      void qc.invalidateQueries({ queryKey: ['audit-task-schedules', task.id] });
    },
    onError: (err: unknown) => {
      const e = err as ApiError;
      message.error(e.message || '接收失敗，請稍後重試');
    },
  });

  /** 不接收案件 Mutation */
  const rejectMutation = useMutation({
    mutationFn: (values: RejectFormValues) =>
      apiClient.post<{ ok: boolean }>(`/api/audit-tasks/${task.id}/reject-dispatch`, {
        note: values.note.trim(),
      }),
    onSuccess: () => {
      message.success('已提交不接收原因並通知管理組');
      setModalOpen(false);
      rejectForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['audit-task-detail', task.id] });
    },
    onError: (err: unknown) => {
      const e = err as ApiError;
      message.error(e.message || '提交失敗，請稍後重試');
    },
  });

  /** 簽名完成回呼：上傳 PNG 取得 fileId。 */
  const handleSignatureSubmit = async (file: File) => {
    try {
      setSigUploading(true);
      const uploaded = await uploadFile(file, {
        scope: 'signature',
        ownerType: 'case_audit_team',
        ownerId: task.caseId,
      });
      setSignatureFileId(uploaded.id);
      message.success('簽名已保存');
      setSigOpen(false);
    } catch (e) {
      // 依規範：異常記錄錯誤（前端 console）
      console.error('dispatch confirm signature upload failed', e);
      message.error(e instanceof Error ? e.message : '簽名上傳失敗');
      throw e;
    } finally {
      setSigUploading(false);
    }
  };

  /** 提交（依 mode 分派 accept / reject） */
  const handleSubmit = async () => {
    if (mode === 'accept') {
      try {
        const values = await acceptForm.validateFields();
        if (!signatureFileId) {
          message.error('請先完成主導稽核員確認簽名');
          return;
        }
        acceptMutation.mutate(values);
      } catch {
        /* 校驗失敗：antd 已展示欄位錯誤 */
      }
    } else {
      try {
        const values = await rejectForm.validateFields();
        rejectMutation.mutate(values);
      } catch {
        /* 校驗失敗 */
      }
    }
  };

  // ─── 已回覆：展示回覆結果摘要 ──────────────────────────────────────────────
  if (alreadyResponded) {
    const accepted = task.dispatchResponse === 'accepted';
    return (
      <Card
        size="small"
        title={
          <Space size={8}>
            {accepted ? (
              <CheckCircle2 size={14} color="#52c41a" />
            ) : (
              <XCircle size={14} color="#faad14" />
            )}
            派案確認
          </Space>
        }
      >
        {accepted ? (
          <Alert
            type="success"
            showIcon
            message="已接收案件"
            description="稽核員已接收派案並反饋管理組（預定稽核日期與稽核人天數請於「稽核計畫」階段填寫或修改）。"
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="不接收案件"
            description={
              <Typography.Text>
                驗證組備註：{task.dispatchResponseNote ?? '（未填）'}
              </Typography.Text>
            }
          />
        )}
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={
        <Space size={8}>
          <CheckCircle2 size={14} />
          派案確認
        </Space>
      }
      extra={
        isAssignee && (
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setMode('accept');
              setModalOpen(true);
            }}
          >
            回覆派案
          </Button>
        )
      }
    >
      {isAssignee ? (
        <Typography.Text type="secondary">
          請確認是否接收本案件：接收需確認稽核小組成員並由主導稽核員簽名（預定稽核日期與稽核人天數請於「稽核計畫」階段填寫）；
          不接收需填驗證組備註說明原因。
        </Typography.Text>
      ) : (
        <Typography.Text type="secondary">待稽核員回覆是否接收案件。</Typography.Text>
      )}

      {/* 回覆 Modal（接收 / 不接收 兩路徑） */}
      <Modal
        title="派案確認"
        open={modalOpen}
        onOk={() => void handleSubmit()}
        confirmLoading={acceptMutation.isPending || rejectMutation.isPending}
        onCancel={() => {
          setModalOpen(false);
          acceptForm.resetFields();
          rejectForm.resetFields();
          setAddedMembers([]);
          setPendingAddUserId(undefined);
        }}
        okText={mode === 'accept' ? '接收並提交' : '提交不接收'}
        width={600}
        destroyOnHidden
      >
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value as 'accept' | 'reject')}
          optionType="button"
          buttonStyle="solid"
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="accept">接收案件</Radio.Button>
          <Radio.Button value="reject">不接收案件</Radio.Button>
        </Radio.Group>

        {mode === 'accept' ? (
          <Form form={acceptForm} layout="vertical" preserve={false}>
            {/* 稽核小組成員（序18：原「稽核小組角色」改名；對本案成員指定角色，並可添加成員） */}
            <Form.Item label="稽核小組成員">
              {teamMembers.length === 0 ? (
                <Typography.Text type="secondary">尚未派遣團隊成員。</Typography.Text>
              ) : (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {teamMembers.map((m) => (
                    <Space key={m.userId} style={{ width: '100%' }} align="center">
                      <span style={{ minWidth: 120, display: 'inline-block' }}>
                        {m.userName ?? `#${m.userId}`}
                        {m.userId === task.assigneeId && (
                          <Tag color="gold" style={{ marginLeft: 6 }}>
                            本人
                          </Tag>
                        )}
                        {isAddedMember(m.userId) && (
                          <Tag color="blue" style={{ marginLeft: 6 }}>
                            新添加
                          </Tag>
                        )}
                      </span>
                      {isAddedMember(m.userId) ? (
                        // 序27：新添加的成員角色固定為稽核員，不在此選擇其他角色
                        <Tag>{DISPATCH_TEAM_MEMBER_ROLE_LABELS.auditor}</Tag>
                      ) : (
                        <Form.Item
                          name={['memberRoles', String(m.userId)]}
                          noStyle
                          initialValue={
                            // 本人為組長任務且尚無明確角色時預設主導稽核員
                            m.userId === task.assigneeId && task.isTeamLead && !m.role
                              ? 'lead'
                              : labelToRole(m.role)
                          }
                        >
                          <Select
                            style={{ width: 140 }}
                            options={DISPATCH_TEAM_MEMBER_ROLES.map((r) => ({
                              value: r,
                              label: DISPATCH_TEAM_MEMBER_ROLE_LABELS[r],
                            }))}
                          />
                        </Form.Item>
                      )}
                      {isAddedMember(m.userId) && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<Trash2 size={14} />}
                          aria-label="移除新添加的成員"
                          onClick={() => handleRemoveAddedMember(m.userId)}
                        />
                      )}
                    </Space>
                  ))}
                </Space>
              )}
              {/* 添加稽核小組成員（序18）：選擇在職員工後加入上方清單，再指定角色 */}
              <Space style={{ marginTop: 12 }} wrap>
                <Select
                  showSearch
                  allowClear
                  style={{ width: 220 }}
                  placeholder="選擇要添加的稽核員"
                  loading={candidatesQuery.isLoading}
                  options={candidateOptions}
                  optionFilterProp="label"
                  value={pendingAddUserId}
                  onChange={(v: number | undefined) => setPendingAddUserId(v)}
                />
                <Button
                  icon={<Plus size={14} />}
                  disabled={pendingAddUserId == null}
                  onClick={handleAddMember}
                >
                  添加稽核小組成員
                </Button>
              </Space>
            </Form.Item>

            {/* 主導稽核員確認簽名（序26：原「確認簽名」改名） */}
            <Form.Item label="主導稽核員確認簽名" required>
              <Space>
                <Button onClick={() => setSigOpen(true)}>
                  {signatureFileId ? '重新簽名' : '手寫簽名'}
                </Button>
                {signatureFileId && <Tag color="success">已簽名</Tag>}
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <Form form={rejectForm} layout="vertical" preserve={false}>
            <Form.Item
              name="note"
              label="驗證組備註（不接收原因）"
              rules={[
                { required: true, message: '請填寫不接收原因' },
                { min: 5, message: '原因至少 5 字', transform: (v: string) => v?.trim() },
              ]}
            >
              <Input.TextArea
                rows={4}
                maxLength={2000}
                showCount
                placeholder="請說明不接收案件的原因"
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* 手寫確認簽名彈窗 */}
      <SignaturePadModal
        open={sigOpen}
        title="主導稽核員確認簽名"
        onCancel={() => setSigOpen(false)}
        loading={sigUploading}
        onSubmit={handleSignatureSubmit}
      />
    </Card>
  );
}
